import { useEffect, useRef, useState } from 'react';

/**
 * CursorSpotlight — Sprint 1 / B2
 *
 * Premium-feel "flashlight" overlay that follows the cursor in dark mode
 * (Linear / Vercel-style). Renders a single fixed-position div with a
 * radial-gradient background and animates via `transform: translate3d(...)`
 * for GPU-accelerated movement (no layout thrash, no per-frame paint of
 * the gradient definition).
 *
 * Behavior
 *   - Active only when <html> has the `.dark` class (observed via MutationObserver).
 *   - Disabled if `prefers-reduced-motion: reduce` is set.
 *   - Disabled on coarse pointers (touch devices).
 *   - Mouse position is sampled on `mousemove` and committed inside a
 *     requestAnimationFrame tick — at most one transform write per frame.
 *
 * Mount it once near the top of the page tree (e.g. HomePage). The element
 * is `pointer-events: none` so it never intercepts clicks.
 *
 * z-index: 5 — sits above section backgrounds (z:0/1, including the Hero
 * ParticleBackground at z:1) but below sticky chrome (Navigation, ChatBubble,
 * modals) which use z-40+/z-50+. Tune here if a new chrome layer is added.
 */

const SPOTLIGHT_RADIUS = 400;             // px — radius of the radial gradient circle
const SPOTLIGHT_COLOR  = '61, 220, 132';  // rgb — brand "Terminal Green" (primary-400 #3ddc84)
const SPOTLIGHT_ALPHA  = 0.06;            // very subtle — premium-feel, not gimmicky

const CursorSpotlight = () => {
  // Active only when the user is in dark mode AND has a fine pointer AND has
  // not opted into reduced motion. Computed once on mount, then maintained
  // by a MutationObserver on <html>'s class list.
  const [isActive, setIsActive] = useState(false);
  const elRef = useRef<HTMLDivElement | null>(null);
  const posRef = useRef({ x: -9999, y: -9999 }); // start off-screen
  const rafRef = useRef<number | null>(null);

  // ---- Capability gates ---------------------------------------------------
  // These never change during a session, so we read them once.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    if (reduceMotion || coarsePointer) {
      // Hard-disable — never render, never attach listeners.
      setIsActive(false);
      return;
    }

    const html = document.documentElement;
    const sync = () => setIsActive(html.classList.contains('dark'));
    sync();

    // Observe theme toggles. useTheme() flips the `.dark` class on <html>.
    const observer = new MutationObserver(sync);
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // ---- Mouse tracking -----------------------------------------------------
  // Only attached when `isActive` is true so we pay zero cost in light mode
  // or on touch devices.
  useEffect(() => {
    if (!isActive) return;

    const onMove = (e: MouseEvent) => {
      posRef.current.x = e.clientX;
      posRef.current.y = e.clientY;
      if (rafRef.current != null) return; // already scheduled this frame
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const el = elRef.current;
        if (!el) return;
        // Center the 2*SPOTLIGHT_RADIUS box on the cursor via translate3d
        // (GPU-compositor friendly — no reflow, no repaint of the gradient).
        const x = posRef.current.x - SPOTLIGHT_RADIUS;
        const y = posRef.current.y - SPOTLIGHT_RADIUS;
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isActive]);

  if (!isActive) return null;

  const size = SPOTLIGHT_RADIUS * 2;

  return (
    <div
      ref={elRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${size}px`,
        height: `${size}px`,
        pointerEvents: 'none',
        zIndex: 5,
        // Single radial gradient, centered in the element. The element itself
        // is then translated to track the cursor — gradient is rasterized once.
        background: `radial-gradient(circle ${SPOTLIGHT_RADIUS}px at center, rgba(${SPOTLIGHT_COLOR}, ${SPOTLIGHT_ALPHA}), transparent 70%)`,
        // Start off-screen until the first mousemove arrives.
        transform: 'translate3d(-9999px, -9999px, 0)',
        willChange: 'transform',
      }}
    />
  );
};

export default CursorSpotlight;
